import { useEffect } from 'react';

import {
  IF_CDN_IFRAME_DOMAIN,
  TI_RESIZABLE_ORIGINS,
  YOUTUBE_IFRAME_DOMAIN,
  YOUTUBE_NOCOOKIE_HOST,
} from '@utils/constants';
import { isAllowedOrigin, readReportedHeight, shouldApplyHeight } from '@utils/iframeResize';

import debounce from '../application/javascripts/debounce';
import useServerSideRendering from './useServerSideRendering';

const IFRAME_RESIZE = 'iframe.resize';

const RESIZABLE_IFRAME_SELECTOR = TI_RESIZABLE_ORIGINS.map(
  (origin) => `iframe[src*="${origin}"]`
).join(', ');

const VIDEO_IFRAME_SELECTOR = `iframe[src*="${IF_CDN_IFRAME_DOMAIN}"], iframe[src*="${YOUTUBE_IFRAME_DOMAIN}"], iframe[src*="${YOUTUBE_NOCOOKIE_HOST}"]`;

const useIframeResizeMessager = () => {
  const hydrated = useServerSideRendering();
  useEffect(() => {
    if (!hydrated) return;

    const resizableIframes = document.querySelectorAll(
      RESIZABLE_IFRAME_SELECTOR
    ) as NodeListOf<HTMLIFrameElement>;
    const videoIframes = document.querySelectorAll(
      VIDEO_IFRAME_SELECTOR
    ) as NodeListOf<HTMLIFrameElement>;

    const resizeIframe = (event: MessageEvent) => {
      if (!isAllowedOrigin(event.origin)) return;

      const height = readReportedHeight(event.data);
      if (height === null) return;

      const targetIframe = Array.from(resizableIframes).find(
        (iframe) => iframe.contentWindow === event.source
      );
      if (!targetIframe) return;

      const currentHeight = Number.parseInt(targetIframe.height, 10) || 0;
      if (!shouldApplyHeight(currentHeight, height)) return;

      targetIframe.height = String(height);
    };

    function transmitIframeResizeMessage() {
      videoIframes.forEach((target) => {
        target.style.aspectRatio = '16/9';
        target.style.height = 'auto';
        target.style.border = 'none';
      });

      resizableIframes.forEach((target) => {
        if (!target.contentWindow || !target.src) return;
        try {
          const targetOrigin = new URL(target.src).origin;
          target.contentWindow.postMessage({ context: IFRAME_RESIZE }, targetOrigin);
        } catch {
          // Invalid src — skip
        }
      });
    }

    function resizeIframeAndTransmitMessage() {
      window.setTimeout(transmitIframeResizeMessage, 10);
    }

    const debouncedResizeIframe = debounce(resizeIframeAndTransmitMessage, 200);

    window.addEventListener('message', resizeIframe, true);
    window.addEventListener('resize', debouncedResizeIframe, true);

    transmitIframeResizeMessage();

    return () => {
      window.removeEventListener('message', resizeIframe, true);
      window.removeEventListener('resize', debouncedResizeIframe, true);
    };
  }, [hydrated]);
};

export default useIframeResizeMessager;

/**
 * Below is code used inside the iframes to communicate back their getBoundingClientRect
 * width and height. The Graphics team adds this to their Illustrator files and
 * it gets extracted via ai2html. See this ticket for more information:
 * {@link https://app.asana.com/0/1207359392432611/1207436000889604/f}
 * Placing here for lack of a better location.
 */
/*
<script type="text/javascript">
  (() => {
    "use strict";

    const ALLOWED_ORIGINS = [
      "http://localhost:3000",
      "https://info-reader-sandbox.herokuapp.com",
      "https://info-reader-staging.herokuapp.com",
      "https://theinformation.com",
      "https://www.theinformation.com",
    ];

    const _container = document.querySelector(".ai2html");
    const parentWindow = window.parent;

    function setIframeDimensions() {
      const boundingRect = _container.getBoundingClientRect();
      const width = Math.floor(boundingRect.width);
      const height = Math.floor(boundingRect.height);
      parentWindow.postMessage(
        {
          context: "iframe.resize",
          name: window.name,
          height,
          width,
        },
        "*"
      );
    }

    function captureMessage(event) {
      if (!ALLOWED_ORIGINS.includes(event.origin) || event.data?.context !== "iframe.resize")
        return;
      setIframeDimensions();
    }

    window.addEventListener("message", captureMessage, true);
  })();
</script>
*/
